import { useEffect, useState } from "react";
import { FaRegUser, FaDog } from "react-icons/fa";
import { IoCalendarOutline, IoTimeOutline } from "react-icons/io5";
import { getAccessToken } from "../../../storage/storage";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import toast from "react-hot-toast";
import { ButtonLoader4 } from "../../../components/ButtonLoader";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import Swal from "sweetalert2";

const ContactFormSchema = Yup.object({
  property: Yup.string().required("Select option"),
  lead_guest_name: Yup.string().required("Enter lead guest name")
  .max(50, "First name must be at most 50 characters")
    .test("not-empty", "First name cannot be only spaces", value => value && value.trim().length > 0)
    .matches(
      /^[A-Za-z\s'-]+$/,
      "First name can only contain letters, spaces, hyphens, and apostrophes"
    )
    .test("min-trimmed-length", "First name must be at least 2 characters", value => value && value.trim().length >= 2),
  
  number_of_guests: Yup.number()
    .typeError("Number of guests must be a number")
    .required("Enter number of guests")
    .positive("Number of guests must be positive")
    .max(20, "Number of guests cannot exceed 20"),

  number_of_pets: Yup.number()
    .typeError("Number of pets must be a number")
    .required("Enter number of pets")
    .min(0, "Number of pets cannot be negative")
    .max(20, "Number of pets cannot exceed 20"),

  check_in_date: Yup.date().required("Enter check-in date"),

  check_out_date: Yup.date()
    .required("Enter check-out date")
    .test(
      "is-after-checkin",
      "Check-out date must be after check-in date",
      function (value) {
        const { check_in_date } = this.parent;
        return value && check_in_date && new Date(value) > new Date(check_in_date);
      }
    ),
  check_in_time: Yup.string().required("Enter check-in time"),
  check_out_time: Yup.string().required("Enter check-out time"),
  eta: Yup.string().required("Enter ETA"),
 // cleaner: Yup.string().required("Select cleaner"),
  
});

export default function GuestStayForm() {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const stay = location.state?.stay; // Stay data from location
  const cleanerEdit = JSON.parse(sessionStorage.getItem("cleanerEdit")); // Retr
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  
  // Get the previous path from location state or default to "/stays"
  const previousPath = location.state?.from || "/stays";

  const initialValues = {
    property: stay?.property || "",
    lead_guest_name: stay?.lead_guest_name || "",
    number_of_guests: stay?.number_of_guests || "",
    number_of_pets: stay?.number_of_pets ?? "", // This will preserve 0
    check_in_date: stay?.check_in_date || "",
    check_out_date: stay?.check_out_date || "",
    check_in_time: stay?.check_in_time || "",
    check_out_time: stay?.check_out_time || "",
    eta: stay?.eta || "",
    cleaner: stay?.cleaner || "",
   
  };

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/properties/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => setOptions(response.data))
      .catch((error) => console.error("Error fetching options:", error));
  }, []);

  const handlePropertyChange = (e) => {
  const id = parseInt(e.target.value);
  setSelectedPropertyId(id);
  setFieldValue("property", id);
};
useEffect(() => {
  if (cleanerEdit && stay?.property) {
    setSelectedPropertyId(stay.property); // numeric ID
  }
  console.log("stay",stay);
  console.log("number_of_pets",values.number_of_pets);
}, [cleanerEdit, stay]);
  useEffect(() => {
    if (selectedPropertyId) {
      // Ensure API calls only when a valid property is selected
      axios
        .get(
          `${BASE_URL}/api/cleaners-by-property/?property_id=${selectedPropertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => setCleaners(response.data))
        .catch((error) => console.error("Error fetching cleaners:", error));
    }
  }, [selectedPropertyId]);

  const { values, errors, handleChange, handleSubmit, setFieldValue, touched } =
    useFormik({
      initialValues: initialValues,
      validationSchema: ContactFormSchema,
      onSubmit: async (values, action) => {
        setLoading(true);

        const payload = {
          ...values,
          property: values.property ? parseInt(values.property) : null,
          cleaner: values.cleaner ? parseInt(values.cleaner) : null, // Ensure integer
        };

        try {
          let response;

          if (cleanerEdit) {
            // If edit mode is true, update the existing stay
            response = await axios.put(
              `${BASE_URL}/api/guest-stay/update/${stay.id}/`,
              payload,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
          } else if (!cleanerEdit) {
            // If not in edit mode, create a new stay
            response = await axios.post(
              `${BASE_URL}/api/guest-stays/`,
              payload,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
          }

          Swal.fire({
            icon: "success",
            title: cleanerEdit
              ? "Data Updated Successfully"
              : response.data.message || "Data Added Successfully",
            timer: 2000,
            showConfirmButton: false,
            position: "center",
          });
          sessionStorage.removeItem("cleanerEdit");
          action.resetForm();
          navigate(previousPath); // Navigate back to previous page
        } catch (error) {
          Swal.fire({
            icon: "error",
            title:
              error.response?.data?.error ||
              error.response?.data?.non_field_errors ||
              "Something went wrong!",
            timer: 2000,
            showConfirmButton: false,
            position: "center",
          });
        } finally {
          setLoading(false);
        }
      },
    });

  useEffect(() => {
    setFieldValue("check_in_time", values.check_in_time || "15:00");
    setFieldValue("check_out_time", values.check_out_time || "10:00");
    setFieldValue("eta", values.eta || "12:00");
  }, [setFieldValue]);

  // Function to handle back navigation
 const handleBackClick = () => {
    // If there's previous state, use it, otherwise go back in history
    if (location.state?.from) {
      navigate(previousPath);
    } else {
      navigate(-1); // Go back one step in history
    }
  };

  return (
    <div className="relative">
      {/* Back Button */}
      <button
        onClick={handleBackClick}
        className="flex items-center gap-2 absolute lg:top-2 top-[-30px] left-6 text-xl font-bold text-[#586989] hover:text-blue-600"
      >
        <IoMdArrowBack />
        <p>Back</p>
      </button>
      <div className="max-w-2xl mx-auto h-fit mt-10 mb-10">
        <div className="bg-white shadow-lg rounded-lg p-6 w-full">
          <h2 className="md:text-3xl text-xl font-bold text-[#586989] text-center">
            Guest Stays
          </h2>
          <p className="text-gray-600 text-center">
            Manage guest stays for your properties
          </p>

          {/* Property Selection */}
          <div className="mt-4 flex flex-col gap-2">
            <label className="font-medium">Select Property</label>
            <select
              name="property"
              value={selectedPropertyId || ""}  // controlled
              onChange={handlePropertyChange}
              className="w-full p-2 border rounded-md"
            >
              <option value="">Select a Property</option>
              {options.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.name}
                </option>
              ))}
            </select>
            {errors.property && touched.property && (
              <div className="text-[#DC2626] text-[12px]">
                {errors.property}
              </div>
            )}
          </div>

          {/* Guest Details Form */}
          <div className="mt-4 bg-[#FFF5CE] rounded">
            <div className="p-4">
              <h3 className="text-lg font-semibold">Add New Guest Stay</h3>

              {/* Lead Guest Name */}
              <div className="mt-2 space-y-3 w-full">
                <label className="block">Lead Guest Name*</label>
                <input
                  placeholder="Enter lead guest name"
                  name="lead_guest_name"
                  value={values.lead_guest_name}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg"
                />
                {errors.lead_guest_name && touched.lead_guest_name && (
                  <div className="text-[#DC2626] text-[12px]">
                    {errors.lead_guest_name}
                  </div>
                )}
              </div>

              {/* Number of Guests & Pets */}
              <div className="flex md:flex-row flex-col gap-4 mt-4">
                <div className="flex-1">
                  <label>Number of Guests*</label>
                  <input
                    type="text"
                    name="number_of_guests"
                    value={values.number_of_guests}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.number_of_guests && touched.number_of_guests && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.number_of_guests}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label>Number of Pets*</label>
                  <input
                    type="text"
                    name="number_of_pets"
                    value={values.number_of_pets}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.number_of_pets && touched.number_of_pets && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.number_of_pets}
                    </div>
                  )}
                </div>
              </div>

              {/* Check-in & Check-out Dates */}
              <div className="flex md:flex-row flex-col gap-4 mt-4">
                <div className="flex-1">
                  <label>Check-in Date*</label>
                  <input
                    type="date"
                    name="check_in_date"
                    value={values.check_in_date}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.check_in_date && touched.check_in_date && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.check_in_date}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label>Check-out Date*</label>
                  <input
                    type="date"
                    name="check_out_date"
                    value={values.check_out_date}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.check_out_date && touched.check_out_date && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.check_out_date}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex md:flex-row flex-col gap-4 mt-4">
                <div className="flex-1">
                  <label>Check-in Time*</label>
                  <input
                    type="time"
                    name="check_in_time"
                    value={values.check_in_time}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.check_in_time && touched.check_in_time && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.check_in_time}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label>Check-out Time*</label>
                  <input
                    type="time"
                    name="check_out_time"
                    value={values.check_out_time}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.check_out_time && touched.check_out_time && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.check_out_time}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label>ETA</label>
                  <input
                    type="time"
                    name="eta"
                    value={values.eta}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                  {errors.eta && touched.eta && (
                    <div className="text-[#DC2626] text-[12px] pt-1">
                      {errors.eta}
                    </div>
                  )}
                </div>
              </div>

              {/* Assign Cleaner */}

              <div className="mt-4">
                <label>Assign Cleaner</label>
                <select
                  name="cleaner"
                  value={values.cleaner}
                  onChange={(e) =>
                    setFieldValue(
                      "cleaner",
                      e.target.value ? parseInt(e.target.value) : ""
                    )
                  }
                  className="w-full p-2 border rounded-md"
                >
                  <option value="">Please select...</option>
                  {cleaners.map((option) => (
                    <option className="" key={option.id} value={option.id}>
                      {option.first_name} {option.surname}
                    </option>
                  ))}
                </select>

                {errors.cleaner && touched.cleaner && (
                  <div className="text-[#DC2626] text-[12px] h-[6px] pb-2 pt-2">
                    {errors.cleaner}
                  </div>
                )}
              </div>

              {/* Submit & Cancel Buttons */}
              <div className="flex justify-end gap-5 mt-6">
                <button
                  onClick={handleBackClick}
                  className="border-2 border-[#106A3A] text-black w-[100px] h-[45px] rounded-md flex items-center justify-center"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  type="submit"
                  className="bg-[#586989] text-white w-[140px] h-[45px] flex items-center justify-center rounded-md"
                >
                  {!cleanerEdit ? (
                    <>{loading ? <ButtonLoader4 /> : <>Add Guest Stay</>}</>
                  ) : (
                    <>{loading ? <ButtonLoader4 /> : <>Update Guest Stay</>}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}